import { ORIGIN_RECEIVER, ORIGIN_SENDER, SEARCH_TYPE_CARE } from "core/consts";
import { getName, getProviderSearchAccountName } from "core/model/accounts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { capitaliseFirst } from "core/model/utils/strings";
import { GetOntologyType, LastSeen, SearchType } from "core/types";
import { GREY_600, ICON_GREY } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, sizing } from "ds_legacy/materials/metrics";
import {
  Caption,
  CaptionProps,
  FONT_SIZE_12,
  FONT_WEIGHT_REGULAR,
} from "ds_legacy/materials/typography";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { HTMLAttributes, useState } from "react";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { v4 as uuidv4 } from "uuid";

const getTranslationForOrigin = (
  getOntology: GetOntologyType,
  translations: Translations,
  origin: number,
  searchType: SearchType | null | undefined,
) => {
  switch (origin) {
    case ORIGIN_RECEIVER:
      return ` - ${capitaliseFirst(
        getOntology({
          key: searchType || SEARCH_TYPE_CARE,
          type: "receiverTypeSingular",
        }),
      )}`;
    case ORIGIN_SENDER:
      return ` - ${translations.messenger.sender.clinic}`;
    default:
      return "";
  }
};

export function SeenTag({
  lastSeens,
  patientId,
  searchType,
}: {
  lastSeens: LastSeen[] | null | undefined;
  patientId: string | undefined;
  searchType?: SearchType | null;
}) {
  const contentId = `content-${uuidv4()}`;
  const translations = useTranslations();
  const [expanded, setExpanded] = useState<boolean>(false);
  const ExpandIcon = expanded ? ChevronUpIcon : ChevronDownIcon;
  const iconStyle = { height: sizing(2), width: sizing(2) };
  const getOntology = useGetOntology();

  const captionProps: CaptionProps & HTMLAttributes<HTMLSpanElement> = {
    color: GREY_600,
    style: {
      alignItems: "flex-end",
      display: "flex",
      flexDirection: "column",
      fontSize: FONT_SIZE_12,
      fontWeight: FONT_WEIGHT_REGULAR,
      margin: margin(0.5, 0, 0, 0),
    },
  };

  if (!lastSeens?.length) {
    return (
      <Caption as="p" {...captionProps}>
        {translations.messenger.notSeen}
      </Caption>
    );
  }

  return (
    <>
      <button
        aria-controls={contentId}
        aria-expanded={expanded}
        aria-label={
          translations.messenger.accessibility.messageSeenNotification
        }
        className="mt-0.5 box-border border-none bg-transparent px-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 focus-visible:outline-solid"
        onClick={() => setExpanded((previousState) => !previousState)}
      >
        <Caption {...captionProps}>
          <div className="flex flex-row items-center">
            {`${translations.messenger.seen} (${lastSeens.length})`}
            <ExpandIcon
              style={{ color: ICON_GREY, ...iconStyle }}
              {...iconStyle}
            />
          </div>
        </Caption>
      </button>
      <VerticalLayout
        id={contentId}
        margin={margin(0.5, 2, 0, 0)}
        visibility={expanded ? "visible" : "hidden"}
      >
        {lastSeens.map((seen) => (
          <Caption
            key={seen.timestamp}
            as="p"
            color={GREY_600}
            style={{
              alignItems: "flex-end",
              display: "flex",
              flexDirection: "column",
              margin: margin(0),
            }}
          >
            {`${getName(seen.account, getOntology, {
              providerSearchAccountName: getProviderSearchAccountName({
                patientId,
                translations,
              }),
              withAcademicTitle: false,
              withSalutation: false,
            })}${getTranslationForOrigin(
              getOntology,
              translations,
              seen.origin,
              searchType,
            )}`}
          </Caption>
        ))}
      </VerticalLayout>
    </>
  );
}
